import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  BarChart3,
  BookOpen,
  List,
  Calendar,
  Scale,
  Settings,
  Upload,
  Plus,
  TrendingUp,
  Search,
  Zap,
} from "lucide-react";

const NAV_ITEMS = [
  { suffix: "", label: "Dashboard", icon: LayoutDashboard },
  { suffix: "statistics", label: "Statistics", icon: BarChart3 },
  { suffix: "trades", label: "Trades", icon: List },
  { suffix: "calendar", label: "Calendar", icon: Calendar },
  { suffix: "comparison", label: "Compare", icon: Scale },
  { suffix: "import", label: "Import", icon: Upload },
  { suffix: "playbook", label: "Playbook", icon: BookOpen },
  { suffix: "settings", label: "Settings", icon: Settings },
];

const QUICK_ACTIONS = [
  { id: "new-trade", label: "Add New Trade", icon: Plus, action: "navigate", target: "" },
  { id: "view-stats", label: "View Statistics", icon: BarChart3, action: "navigate", target: "statistics" },
  { id: "view-trades", label: "View All Trades", icon: List, action: "navigate", target: "trades" },
];

export default function CommandPalette({ mode, open, onOpenChange }) {
  const navigate = useNavigate();

  const buildPath = useCallback(
    (suffix = "") => (suffix ? `/${mode}/${suffix}` : `/${mode}`),
    [mode]
  );

  const handleSelect = useCallback(
    (item) => {
      onOpenChange(false);
      if (item.action === "navigate") {
        navigate(buildPath(item.target));
      }
    },
    [navigate, buildPath, onOpenChange]
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Pages">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.suffix || "dashboard"}
                onSelect={() => handleSelect({ action: "navigate", target: item.suffix })}
                className="cursor-pointer"
              >
                <Icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
                <CommandShortcut>⌘{item.suffix ? item.suffix[0].toUpperCase() : "D"}</CommandShortcut>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Quick Actions">
          {QUICK_ACTIONS.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.id}
                onSelect={() => handleSelect(item)}
                className="cursor-pointer"
              >
                <Icon className="mr-2 h-4 w-4 text-primary" />
                <span>{item.label}</span>
                <CommandShortcut>
                  <Zap className="h-3 w-3" />
                </CommandShortcut>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Navigation">
          <CommandItem
            onSelect={() => {
              onOpenChange(false);
              navigate(buildPath("settings"));
            }}
            className="cursor-pointer"
          >
            <Settings className="mr-2 h-4 w-4" />
            <span>Strategy Settings</span>
            <CommandShortcut>⌘,</CommandShortcut>
          </CommandItem>
          <CommandItem
            onSelect={() => {
              onOpenChange(false);
              navigate(buildPath("trades"));
            }}
            className="cursor-pointer"
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            <span>Recent Trades</span>
            <CommandShortcut>⌘T</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
